import { Text } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import type { RefObject } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box3,
  CylinderGeometry,
  type Group,
  type Mesh,
  MeshBasicMaterial,
  type Object3D,
  Vector3,
} from "three";

function shouldExcludeFromBoundingBox(obj: Object3D): boolean {
  let current: Object3D | null = obj;
  while (current) {
    if (current.userData?.isMeasurementBox || current.userData?.isDragHelper) {
      return true;
    }
    current = current.parent;
  }
  return false;
}

/** Bounding box in local coordinates relative to case origin (target position). */
function calculateBoundingBoxExcludingMeasurements(
  object: Object3D,
  caseOrigin: Vector3,
): Box3 {
  const box = new Box3();
  let hasGeometry = false;

  const traverse = (obj: Object3D) => {
    if (shouldExcludeFromBoundingBox(obj)) {
      return;
    }

    if ("geometry" in obj) {
      const meshLike = obj as Mesh;
      if (meshLike.geometry) {
        if (meshLike.matrixWorldNeedsUpdate) {
          meshLike.updateMatrixWorld();
        }

        const geometryBox = new Box3();
        geometryBox.setFromObject(meshLike);
        box.union(geometryBox);
        hasGeometry = true;
      }
    }

    for (const child of obj.children) {
      traverse(child);
    }
  };

  traverse(object);

  if (!hasGeometry) {
    return new Box3();
  }

  const localBox = new Box3();
  localBox.min.copy(box.min).sub(caseOrigin);
  localBox.max.copy(box.max).sub(caseOrigin);

  return localBox;
}

export type MeasurementRendererProps = {
  target?: RefObject<Object3D | null> | null;
  visible?: boolean;
  onBoundingBoxCalculated?: () => void;
  onBoundingBoxReady?: (boundingBox: {
    x: number;
    y: number;
    z: number;
  }) => void;
  pdfPreview?: boolean; // If true, use larger sizes for PDF preview
};

export const MeasurementRenderer = ({
  target,
  visible = true,
  onBoundingBoxCalculated,
  onBoundingBoxReady,
  pdfPreview = false,
}: Readonly<MeasurementRendererProps>) => {
  const [boundingBox, setBoundingBox] = useState<{
    size: Vector3;
    center: Vector3;
  } | null>(null);

  // Continuous RAF loop keeps bounds in sync during animations and part changes.
  useEffect(() => {
    if (!target?.current) {
      setBoundingBox(null);
      return;
    }

    let animationFrameId: number | null = null;
    let lastBoundingBoxHash: string | null = null;
    let hasValidBoundingBox = false;

    const calculateBoundingBox = () => {
      if (!target?.current) return;

      // Check if model has meaningful geometry
      const hasGeometry = target.current.children.some((child: Object3D) => {
        if (
          ("geometry" in child && (child as Mesh).geometry) ||
          child.children.length > 0
        ) {
          return true;
        }
        return false;
      });

      if (!hasGeometry) {
        return;
      }

      // Calculate bounding box manually, excluding all measurement boxes
      // This avoids flickering since we don't modify the scene graph
      // Get case origin position (world coordinates of the case root)
      target.current.updateMatrixWorld();
      const caseOrigin = new Vector3();
      caseOrigin.setFromMatrixPosition(target.current.matrixWorld);

      // Calculate bounding box in local coordinates relative to case origin
      const box = calculateBoundingBoxExcludingMeasurements(
        target.current,
        caseOrigin,
      );
      const size = new Vector3();
      box.getSize(size);

      // Only update if we have a valid bounding box
      if (size.x > 0 && size.y > 0 && size.z > 0) {
        // Use the actual min/max positions to calculate the true center
        const boundingBoxCenter = new Vector3(
          (box.min.x + box.max.x) / 2,
          (box.min.y + box.max.y) / 2,
          (box.min.z + box.max.z) / 2,
        );

        // Create a hash to detect if bounding box actually changed
        // This prevents unnecessary state updates and re-renders
        const currentHash = `${box.min.x.toFixed(2)},${box.min.y.toFixed(2)},${box.min.z.toFixed(2)},${box.max.x.toFixed(2)},${box.max.y.toFixed(2)},${box.max.z.toFixed(2)}`;

        if (currentHash !== lastBoundingBoxHash) {
          lastBoundingBoxHash = currentHash;
          setBoundingBox({ size, center: boundingBoxCenter });

          // Notify that bounding box has been calculated (only once on first valid calculation)
          if (!hasValidBoundingBox && onBoundingBoxCalculated) {
            hasValidBoundingBox = true;
            onBoundingBoxCalculated();
          }

          // Call onBoundingBoxReady with the calculated size in mm
          if (onBoundingBoxReady) {
            onBoundingBoxReady({
              x: size.x * 1000, // Convert from meters to mm
              y: size.y * 1000,
              z: size.z * 1000,
            });
          }
        }
      }
    };

    // Use requestAnimationFrame for continuous updates
    // This automatically adapts to the browser's refresh rate (typically 60fps)
    // and ensures the bounding box updates smoothly during animations
    const updateLoop = () => {
      calculateBoundingBox();
      animationFrameId = requestAnimationFrame(updateLoop);
    };

    // Start continuous animation frame loop
    // This will run at the browser's native refresh rate (usually 60fps)
    animationFrameId = requestAnimationFrame(updateLoop);

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [target, onBoundingBoxCalculated, onBoundingBoxReady]);

  if (!visible || !boundingBox) {
    return null;
  }

  return (
    <group userData={{ isMeasurementBox: true }}>
      <group
        position={
          [
            boundingBox.center.x,
            boundingBox.center.y,
            boundingBox.center.z,
          ] as [number, number, number]
        }
      >
        <CoordinateAxes boundingBox={boundingBox} pdfPreview={pdfPreview} />
      </group>
    </group>
  );
};

// Component for rendering coordinate axes as cylinders
const CoordinateAxes = ({
  boundingBox,
  pdfPreview = false,
}: {
  boundingBox: { size: Vector3; center: Vector3 };
  pdfPreview?: boolean;
}) => {
  const { camera } = useThree();
  const cylinderRadius = pdfPreview ? 3 : 1; // Thicker cylinders for PDF preview

  // Convert bounding box from meters to millimeters (centralized conversion)
  // boundingBox comes in meters (Three.js standard), convert to mm once at the start
  const sizeMm = new Vector3(
    boundingBox.size.x * 1000,
    boundingBox.size.y * 1000,
    boundingBox.size.z * 1000,
  );
  const centerMm = new Vector3(
    boundingBox.center.x * 1000,
    boundingBox.center.y * 1000,
    boundingBox.center.z * 1000,
  );

  // Calculate half dimensions in millimeters
  const halfSizeX = sizeMm.x / 2; // Half width
  const halfSizeY = sizeMm.y / 2; // Half height
  const halfSizeZ = sizeMm.z / 2; // Half depth

  // Refs for label groups (Troika text) to update their positions dynamically
  const xLabelRef = useRef<Group | null>(null);
  const zLabelRef = useRef<Group | null>(null);
  const yLabelRef = useRef<Group | null>(null);
  const xCylinderRef = useRef<Mesh | null>(null);
  const zCylinderRef = useRef<Mesh | null>(null);
  const yCylinderRef = useRef<Mesh | null>(null);
  const groupRef = useRef<Group | null>(null);

  // Cache camera position to only update when camera moves
  const lastCameraPosition = useRef<Vector3 | null>(null);
  const updateThreshold = 0.001; // Update if camera moved more than 1mm

  // Reusable function to update axis positions based on camera angle
  const updateAxisPositions = useCallback(() => {
    if (
      !xLabelRef.current ||
      !zLabelRef.current ||
      !yLabelRef.current ||
      !xCylinderRef.current ||
      !zCylinderRef.current ||
      !yCylinderRef.current ||
      !groupRef.current
    ) {
      return;
    }

    // Get camera position in world coordinates
    const cameraPosition = new Vector3();
    camera.getWorldPosition(cameraPosition);

    // Get center position in world coordinates
    const centerWorld = new Vector3();
    centerWorld.setFromMatrixPosition(groupRef.current.matrixWorld);

    // Calculate direction from center to camera in world coordinates
    const directionToCamera = new Vector3().subVectors(
      cameraPosition,
      centerWorld,
    );

    // Calculate azimuth angle (rotation around Y-axis) for Y-axis positioning
    // atan2(x, z) gives us the angle in the XZ plane
    // 0° = front (+Z), 90° = right (+X), 180° = back (-Z), 270° = left (-X)
    const azimuth = Math.atan2(directionToCamera.x, directionToCamera.z);

    // Determine X-axis side directly from X component
    // If camera is on the right (x > 0), show left side (-1)
    // If camera is on the left (x < 0), show right side (+1)
    const xAxisSide = directionToCamera.x > 0 ? -1 : 1; // -1 = left, +1 = right

    // Determine Z-axis side directly from Z component
    // If camera is in front (z > 0), show back side (-1)
    // If camera is in back (z < 0), show front side (+1)
    const zAxisSide = directionToCamera.z > 0 ? -1 : 1; // -1 = back, +1 = front

    // X/Z axes top vs bottom: from above (camera Y > center) -> show on top; from below -> show on bottom
    const cameraFromAbove = directionToCamera.y > 0;
    const xzVerticalSide = cameraFromAbove ? 1 : -1; // +1 = top (center + halfSizeY), -1 = bottom (center - halfSizeY)
    const xzLabelOffsetY = cameraFromAbove ? 20 : -20; // label above or below the edge

    // Determine Y-axis corner position (one of the two back corners, closest to camera)
    // Divide azimuth into 8 sectors (45° each) to determine which back corner to use
    // Normalize azimuth to [0, 2π] range
    const normalizedAzimuth = azimuth < 0 ? azimuth + Math.PI * 2 : azimuth;
    const sector = Math.floor((normalizedAzimuth / (Math.PI * 2)) * 8) % 8;

    // Back corners are at z = centerMm.z - halfSizeZ (hinten)
    // Choose between left (-halfSizeX) and right (+halfSizeX) based on sector
    // Sectors: 0-3 = front/right area -> back-left corner (hinten links)
    //          4-7 = back/left area -> back-right corner (hinten rechts)
    const yAxisSideX =
      sector === 0 || sector === 3 || sector === 5 || sector === 6 ? -1 : 1;
    const yAxisSideZ =
      sector === 1 || sector === 3 || sector === 4 || sector === 6 ? -1 : 1;

    // X-axis: at the far side in Z, and top or bottom depending on camera
    const xAxisCenterY = centerMm.y + halfSizeY * xzVerticalSide;
    const xAxisCenterZ = centerMm.z + halfSizeZ * zAxisSide;
    xCylinderRef.current.position.set(centerMm.x, xAxisCenterY, xAxisCenterZ);
    xLabelRef.current.position.set(
      centerMm.x,
      xAxisCenterY + xzLabelOffsetY,
      xAxisCenterZ + 20 * zAxisSide,
    );

    // Z-axis: at the far side in X, and top or bottom depending on camera
    const zAxisCenterY = centerMm.y + halfSizeY * xzVerticalSide;
    const zAxisCenterX = centerMm.x + halfSizeX * xAxisSide;
    zCylinderRef.current.position.set(zAxisCenterX, zAxisCenterY, centerMm.z);
    zLabelRef.current.position.set(
      zAxisCenterX + 20 * xAxisSide,
      zAxisCenterY + xzLabelOffsetY,
      centerMm.z,
    );

    // Y-axis center point: at the back corner closest to camera
    const yAxisCornerX = centerMm.x + halfSizeX * yAxisSideX;
    const yAxisCenterY = centerMm.y; // Center along Y-axis
    const yAxisCornerZ = centerMm.z + halfSizeZ * yAxisSideZ;
    yCylinderRef.current.position.set(yAxisCornerX, yAxisCenterY, yAxisCornerZ);
    yLabelRef.current.position.set(
      yAxisCornerX + 40 * yAxisSideX,
      yAxisCenterY,
      yAxisCornerZ + 40 * yAxisSideZ,
    );
  }, [camera, centerMm, halfSizeX, halfSizeY, halfSizeZ]);

  // Update positions when bounding box changes (updateAxisPositions identity tracks bbox via useCallback deps)
  useEffect(() => {
    updateAxisPositions();
  }, [updateAxisPositions]);

  // Calculate camera-relative positioning and billboard labels (face camera)
  useFrame(() => {
    // Get camera position in world coordinates
    const cameraPosition = new Vector3();
    camera.getWorldPosition(cameraPosition);

    // Check if camera moved significantly
    if (lastCameraPosition.current) {
      const distance = cameraPosition.distanceTo(lastCameraPosition.current);
      if (distance < updateThreshold) {
        return; // Skip update if camera didn't move much
      }
    }
    lastCameraPosition.current = cameraPosition.clone();

    updateAxisPositions();

    // Billboard: copy camera quaternion so labels stay screen-aligned (always horizontal)
    [xLabelRef.current, yLabelRef.current, zLabelRef.current].forEach(
      (group) => {
        if (group) {
          group.quaternion.copy(camera.quaternion);
        }
      },
    );
  });

  // Create geometries and materials with useMemo to update when bounding box changes
  const xGeometry = useMemo(
    () => new CylinderGeometry(cylinderRadius, cylinderRadius, sizeMm.x),
    [cylinderRadius, sizeMm.x],
  );
  const yGeometry = useMemo(
    () => new CylinderGeometry(cylinderRadius, cylinderRadius, sizeMm.y),
    [cylinderRadius, sizeMm.y],
  );
  const zGeometry = useMemo(
    () => new CylinderGeometry(cylinderRadius, cylinderRadius, sizeMm.z),
    [cylinderRadius, sizeMm.z],
  );

  const redMaterial = useMemo(
    () => new MeshBasicMaterial({ color: "red" }),
    [],
  );
  const blueMaterial = useMemo(
    () => new MeshBasicMaterial({ color: "blue" }),
    [],
  );
  const greenMaterial = useMemo(
    () => new MeshBasicMaterial({ color: "green" }),
    [],
  );

  // Troika text font size in scene units (1 unit = 1mm)
  const labelFontSize = pdfPreview ? 72 : 36;

  return (
    <group ref={groupRef}>
      {/* X-axis cylinder (RED) - dynamically positioned based on camera angle */}
      <mesh
        ref={xCylinderRef}
        geometry={xGeometry}
        material={redMaterial}
        rotation={[0, 0, Math.PI / 2]}
      />
      {/* X-axis length label (Troika) */}
      <group
        ref={(ref) => {
          xLabelRef.current = ref;
        }}
      >
        <Text
          fontSize={labelFontSize}
          color="red"
          anchorX="center"
          anchorY="middle"
        >
          {`${Math.round(sizeMm.x)}mm`}
        </Text>
      </group>

      {/* Y-axis cylinder (BLUE) - dynamically positioned based on camera angle */}
      <mesh
        ref={yCylinderRef}
        geometry={yGeometry}
        material={blueMaterial}
        rotation={[0, 0, 0]}
      />
      {/* Y-axis length label (Troika) */}
      <group
        ref={(ref) => {
          yLabelRef.current = ref;
        }}
      >
        <Text
          fontSize={labelFontSize}
          color="blue"
          anchorX="center"
          anchorY="middle"
        >
          {`${Math.round(sizeMm.y)}mm`}
        </Text>
      </group>

      {/* Z-axis cylinder (GREEN) - dynamically positioned based on camera angle */}
      <mesh
        ref={zCylinderRef}
        geometry={zGeometry}
        material={greenMaterial}
        rotation={[Math.PI / 2, 0, 0]}
      />
      {/* Z-axis length label (Troika) */}
      <group
        ref={(ref) => {
          zLabelRef.current = ref;
        }}
      >
        <Text
          fontSize={labelFontSize}
          color="green"
          anchorX="center"
          anchorY="middle"
        >
          {`${Math.round(sizeMm.z)}mm`}
        </Text>
      </group>
    </group>
  );
};

export default MeasurementRenderer;
